import {
  createReference,
  getExtensionValue,
  getReferenceString,
  MedplumClient,
} from "@medplum/core";
import {
  CodeableConcept,
  Coding,
  DiagnosticReport,
  EpisodeOfCare,
  Observation,
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponseItem,
  ServiceRequest,
} from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";
import { DiagnosticServiceSectionID, ObservationCategory } from "@/fhir/enums/fhir-valuesets";
import { LOINCCodes } from "@/fhir/enums/loinc";
import { QuiltedHealthCodeSystems, QuiltedHealthValueSets } from "@/fhir/enums/quilted-health";
import { SNOMEDCodes } from "@/fhir/enums/snomed";
import { Systems } from "@/fhir/enums/systems";
import {
  HealthGorillaLabOrderInternalStatus,
  HealthGorillaLabOrderInternalStatusDisplay,
  UploadedResultType,
} from "@/health-gorilla/types/labs";
import { fetchPregnancyRecordList, upsertLabOrderToPregnancyRecord } from "@/pregnancy";
import { setAccounts } from "@/utils/accounts";
import { getCodeableConceptCode } from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";
import { getQuestionnaireResponseFromFormValues } from "@/utils/questionnaires";
import { createAttachmentWithHash } from "@/utils/uploads";

import { LAB_TEST_QUESTIONNAIRE_URLS } from "../constants/lab-test-questionnaires";
import { AddManualResultsFormType } from "../zod-schemas/labs";

import { appendServiceRequestToEpisodeOfCareReferrals } from "./labs";

async function fetchLabQuestionnaire(
  medplum: MedplumClient,
  testCode: string,
): Promise<Questionnaire | undefined> {
  const url = LAB_TEST_QUESTIONNAIRE_URLS[testCode];
  if (!url) return undefined;

  const results = await medplum.searchResources("Questionnaire", { url });
  return results[0];
}

interface UploadManualResultInput {
  patient: Patient;
  files: File[];
  resultType: UploadedResultType;
  /** The lab tests or panels, or the imaging studies, the result is for. */
  testOrPanels: string[];
  /** Imaging only: a free-text name shown beside the study. */
  title?: string;
  /** Imaging only: why the study was ordered. Stored as `ServiceRequest.reasonCode.text`. */
  indication?: string;
  /** Imaging only: the reading of the study. Stored as `DiagnosticReport.conclusion`. */
  results?: string;
  requestor?: Practitioner;
  episodeOfCare?: EpisodeOfCare;
  dateOrdered?: Date | null;
  dateReceived?: Date | null;
}

function toOptionalText(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Saves an uploaded lab or imaging result as a reviewed order plus a report holding the files.
 *
 * Imaging results keep the laboratory category, so they appear wherever uploaded lab results do,
 * and add an imaging category so they can be told apart.
 */
export async function uploadManualResult(medplum: MedplumClient, input: UploadManualResultInput) {
  try {
    const {
      patient,
      files,
      resultType,
      testOrPanels,
      requestor,
      episodeOfCare,
      dateOrdered,
      dateReceived,
    } = input;
    const isImaging = resultType === UploadedResultType.Imaging;
    const codeSystem = isImaging
      ? QuiltedHealthValueSets.ImagingStudies
      : QuiltedHealthValueSets.ManualLabTests;
    const indication = isImaging ? toOptionalText(input.indication) : undefined;
    const conclusion = isImaging ? toOptionalText(input.results) : undefined;
    const title = isImaging ? toOptionalText(input.title) : undefined;

    // 1. Upload File
    const attachments = await Promise.all(
      files.map((file) =>
        createAttachmentWithHash(medplum, {
          data: file,
          filename: file.name,
          contentType: file.type,
        }),
      ),
    );

    // 2. Create ServiceRequest (Lab Order)
    const serviceRequest = await medplum.createResource<ServiceRequest>({
      resourceType: "ServiceRequest",
      status: "completed",
      intent: "order",
      subject: createReference(patient),
      requester: requestor ? createReference(requestor) : undefined,
      authoredOn: dateOrdered?.toISOString() ?? new Date().toISOString(),
      occurrenceDateTime: dateReceived?.toISOString(),
      code: {
        coding: testOrPanels.map((testOrPanel) => ({
          system: codeSystem,
          code: testOrPanel,
          display: testOrPanel,
        })),
        text: testOrPanels.join(", "),
      },
      reasonCode: indication ? [{ text: indication }] : undefined,
      extension: title ? [{ url: Extensions.UploadedResultTitle, valueString: title }] : undefined,
      category: [
        {
          coding: [{ system: Systems.SNOMED, code: SNOMEDCodes.LaboratoryProcedure }],
        },
        ...(isImaging
          ? [{ coding: [{ system: Systems.SNOMED, code: SNOMEDCodes.ImagingProcedure }] }]
          : []),
        {
          coding: [
            {
              system: QuiltedHealthCodeSystems.HealthGorillaLabOrderInternalStatus,
              code: HealthGorillaLabOrderInternalStatus.Reviewed,
              display: "Reviewed",
            },
          ],
        },
      ],
    });

    // 3. Link ServiceRequest to the active pregnancy record (if any)
    if (patient.id) {
      const activePregnancyRecords = await fetchPregnancyRecordList(
        medplum,
        getReferenceString(patient)!,
        { status: "current" },
      );
      const activePregnancyRecord = activePregnancyRecords?.[0];
      if (activePregnancyRecord) {
        await upsertLabOrderToPregnancyRecord(medplum, activePregnancyRecord, serviceRequest);
      }
    }

    if (episodeOfCare) {
      await appendServiceRequestToEpisodeOfCareReferrals(medplum, episodeOfCare, serviceRequest);
    }

    // 4. Create DiagnosticReport with presentedForm
    await medplum.createResource<DiagnosticReport>({
      resourceType: "DiagnosticReport",
      status: "final",
      code: {
        coding: testOrPanels.map((testOrPanel) => ({
          system: codeSystem,
          code: testOrPanel,
          display: testOrPanel,
        })),
        text: testOrPanels.join(", "),
      },
      conclusion,
      subject: createReference(patient),
      basedOn: [createReference(serviceRequest)],
      effectiveDateTime: dateReceived?.toISOString(),
      issued: dateReceived?.toISOString() ?? new Date().toISOString(),
      performer: requestor ? [createReference(requestor)] : undefined,
      presentedForm: attachments,
    });

    return serviceRequest;
  } catch (error) {
    showSystemErrorNotification();
    throw error;
  }
}

export async function addManualResults(
  medplum: MedplumClient,
  patient: Patient,
  practitioner: Practitioner,
  files: File[],
  values: AddManualResultsFormType,
  testOptions: { value: string; label: string }[],
): Promise<ServiceRequest | undefined> {
  // Upload all files and collect attachments
  const attachments = await Promise.all(
    files.map((file) =>
      createAttachmentWithHash(medplum, {
        data: file,
        filename: file.name,
        contentType: file.type,
      }),
    ),
  );

  // Fetch all questionnaires for the selected tests
  const questionnaireMap = new Map<string, Questionnaire>();
  await Promise.all(
    values.testsOrPanels.map(async (testCode) => {
      const questionnaire = await fetchLabQuestionnaire(medplum, testCode);
      if (questionnaire) {
        questionnaireMap.set(testCode, questionnaire);
      }
    }),
  );

  // Build a combined questionnaire from fetched questionnaires
  const combinedQuestionnaireItems: QuestionnaireItem[] = values.testsOrPanels
    .map((testCode) => {
      const questionnaire = questionnaireMap.get(testCode);
      const testLabel = testOptions.find((o) => o.value === testCode)?.label || testCode;
      if (questionnaire?.item) {
        return {
          linkId: testCode,
          type: "group",
          text: testLabel,
          item: questionnaire.item,
        } as QuestionnaireItem;
      }
      return undefined;
    })
    .filter((item): item is NonNullable<typeof item> => !!item);

  if (combinedQuestionnaireItems.length === 0) return undefined;

  const questionnaire: Questionnaire = {
    resourceType: "Questionnaire",
    status: "active",
    item: combinedQuestionnaireItems,
  };

  const response = getQuestionnaireResponseFromFormValues(
    questionnaire,
    values.results || {},
    createReference(patient),
    createReference(practitioner),
  );

  if (response.item) {
    const allTestCodes = values.testsOrPanels;
    const allTestLabels = allTestCodes.map(
      (code) => testOptions.find((option) => option.value === code)?.label || code,
    );

    const serviceRequest = await medplum.createResource<ServiceRequest>({
      resourceType: "ServiceRequest",
      status: "completed",
      intent: "order",
      category: [
        {
          coding: [
            {
              system: Systems.SNOMED,
              code: SNOMEDCodes.LaboratoryProcedure,
              display: "Laboratory procedure",
            },
          ],
        },
        {
          coding: [
            {
              system: QuiltedHealthCodeSystems.HealthGorillaLabOrderInternalStatus,
              code: HealthGorillaLabOrderInternalStatus.NeedsReview,
              display:
                HealthGorillaLabOrderInternalStatusDisplay[
                  HealthGorillaLabOrderInternalStatus.NeedsReview
                ],
            },
          ],
        },
      ],
      code: {
        text: allTestLabels.join(", "),
        coding: allTestCodes.map((code) => ({
          system: QuiltedHealthValueSets.ManualLabTests,
          code,
          display: testOptions.find((option) => option.value === code)?.label || code,
        })),
      },
      subject: createReference(patient),
      requester: values.requester ? createReference(values.requester) : undefined,
      authoredOn: values.dateOrdered?.toISOString(),
      occurrenceDateTime: values.dateReceived?.toISOString(),
    });

    await setAccounts(medplum, serviceRequest, [], { extractFromPatient: true });

    // Link ServiceRequest to the active pregnancy record (if any)
    if (patient.id) {
      const activePregnancyRecords = await fetchPregnancyRecordList(
        medplum,
        getReferenceString(patient)!,
        { status: "current" },
      );
      const activePregnancyRecord = activePregnancyRecords?.[0];
      if (activePregnancyRecord) {
        await upsertLabOrderToPregnancyRecord(medplum, activePregnancyRecord, serviceRequest);
      }
    }

    if (values.episodeOfCare) {
      await appendServiceRequestToEpisodeOfCareReferrals(
        medplum,
        values.episodeOfCare as EpisodeOfCare,
        serviceRequest,
      );
    }

    const parentObservations = (
      await Promise.all(
        response.item.map((testGroup) =>
          processLabTest(medplum, patient, testGroup, values, serviceRequest, questionnaireMap),
        ),
      )
    ).filter((obs): obs is NonNullable<typeof obs> => !!obs);

    const diagnosticReport = await medplum.createResource<DiagnosticReport>({
      resourceType: "DiagnosticReport",
      status: "final",
      code: {
        text: allTestLabels.join(", "),
        coding: allTestCodes.map((code) => ({
          system: QuiltedHealthValueSets.ManualLabTests,
          code,
          display: testOptions.find((o) => o.value === code)?.label || code,
        })),
      },
      subject: createReference(patient),
      basedOn: [createReference(serviceRequest)],
      effectiveDateTime: values.dateReceived?.toISOString(),
      issued: values.dateReceived?.toISOString(),
      performer: values.requester ? [createReference(values.requester)] : undefined,
      result: parentObservations.map(createReference),
      presentedForm: attachments,
      category: [
        {
          coding: [
            {
              system: Systems.HL7DiagnosticServiceSectionID,
              code: DiagnosticServiceSectionID.Laboratory,
              display: "Laboratory",
            },
          ],
        },
      ],
    });

    await setAccounts(medplum, diagnosticReport, [], { extractFromPatient: true });

    return serviceRequest;
  }
  return undefined;
}

export function mergeBloodTypeTests(labTests: Observation[]): Observation[] {
  const aboTestIndex = labTests.findIndex(
    (test) => getCodeableConceptCode(test.code) === LOINCCodes.ABOGroup,
  );

  const rhTestIndex = labTests.findIndex(
    (test) => getCodeableConceptCode(test.code) === LOINCCodes.RhType,
  );

  if (aboTestIndex !== -1 && rhTestIndex !== -1 && aboTestIndex !== rhTestIndex) {
    const aboTest = labTests[aboTestIndex];
    const rhTest = labTests[rhTestIndex];

    const mergedMembers = [...(aboTest.hasMember ?? []), ...(rhTest.hasMember ?? [])];

    const syntheticMembers = [...mergedMembers];

    if (!aboTest.hasMember || aboTest.hasMember.length === 0) {
      if (aboTest.valueString || aboTest.valueCodeableConcept || aboTest.valueQuantity) {
        syntheticMembers.push(createReference(aboTest));
      }
    }
    if (!rhTest.hasMember || rhTest.hasMember.length === 0) {
      if (rhTest.valueString || rhTest.valueCodeableConcept || rhTest.valueQuantity) {
        syntheticMembers.push(createReference(rhTest));
      }
    }

    // Create a synthetic "Blood Type" main panel
    const syntheticTest: Observation = {
      ...aboTest,
      id: "merged-blood-type-panel",
      code: {
        coding: [
          {
            system: Systems.LOINC,
            code: LOINCCodes.BloodTypePanel,
            display: "Blood Type",
          },
        ],
      },
      hasMember: syntheticMembers,
    };

    const newTests = [...labTests];
    const indicesToRemove = [aboTestIndex, rhTestIndex].sort((a, b) => b - a);
    indicesToRemove.forEach((index) => newTests.splice(index, 1));

    const insertIndex = Math.min(aboTestIndex, rhTestIndex);
    newTests.splice(insertIndex, 0, syntheticTest);

    return newTests;
  }

  return labTests;
}

const extractAnswerValue = (responseItem: QuestionnaireResponseItem | undefined) =>
  responseItem?.answer?.[0]?.valueQuantity?.value ??
  responseItem?.answer?.[0]?.valueString ??
  responseItem?.answer?.[0]?.valueDecimal;

const findNestedItemBySuffix = (items: QuestionnaireResponseItem[] | undefined, suffix: string) =>
  items?.find((nestedItem) => nestedItem.linkId.endsWith(suffix));

/**
 * Builds the observation display for coded results with an optional additional-info field.
 * "Positive: see additional info" becomes "Positive: {additionalInfo}" (or "Positive: "
 * when empty); bare "See additional info" becomes the entered text (or empty).
 */
function formatCodedResultWithAdditionalInfo(
  resultCoding: Coding,
  additionalInfo?: string,
): string {
  const display = resultCoding.display ?? "";

  if (!resultCoding.code?.endsWith("see-additional-info")) {
    if (additionalInfo) {
      return `${display}: ${additionalInfo}`;
    }

    return display;
  }

  const prefixedMatch = display.match(/^(.+:\s*)see additional info$/i);
  if (prefixedMatch) {
    return `${prefixedMatch[1]}${additionalInfo ?? ""}`;
  }

  return additionalInfo ?? display;
}

const processLabTest = async (
  medplum: MedplumClient,
  patient: Patient,
  testGroup: QuestionnaireResponseItem,
  formValues: AddManualResultsFormType,
  serviceRequest: ServiceRequest,
  questionnaireMap: Map<string, Questionnaire>,
) => {
  const testCode = testGroup.linkId;
  const testLabel = testGroup.text || testCode;
  const questionnaire = questionnaireMap.get(testCode);
  const questionnaireItems = questionnaire?.item;

  if (!questionnaireItems) return undefined;

  const observationPromises =
    testGroup.item?.map(async (responseItem) => {
      const questionnaireDefinition = questionnaireItems.find(
        (questionnaireItem) => questionnaireItem.linkId === responseItem.linkId,
      );
      if (!questionnaireDefinition) return undefined;

      if (questionnaireDefinition.type === "group") {
        return processGroupItem(
          medplum,
          patient,
          serviceRequest,
          responseItem,
          questionnaireDefinition,
          formValues.dateReceived ?? undefined,
        );
      }

      const unit = getExtensionValue(
        questionnaireDefinition,
        Extensions.QuestionnaireFieldRightSection,
      ) as string;

      return createManualObservation(
        medplum,
        patient,
        serviceRequest,
        questionnaireDefinition.text || testLabel,
        extractAnswerValue(responseItem),
        formValues.dateReceived ?? undefined,
        unit,
        undefined,
        undefined,
        {
          system: QuiltedHealthCodeSystems.ManualLabResults,
          code: questionnaireDefinition.linkId,
          display: questionnaireDefinition.text || testLabel,
        },
      );
    }) || [];

  const childObservations = (await Promise.all(observationPromises)).filter(
    (observation): observation is NonNullable<typeof observation> => !!observation,
  );

  if (childObservations.length === 0) return undefined;

  return medplum.createResource<Observation>({
    resourceType: "Observation",
    status: "final",
    category: [
      {
        coding: [
          {
            system: Systems.HL7ObservationCategory,
            code: ObservationCategory.Laboratory,
            display: "Laboratory",
          },
        ],
        text: "Laboratory",
      },
    ],
    code: {
      text: testLabel,
      coding: [
        {
          system: QuiltedHealthValueSets.ManualLabTests,
          code: testCode,
          display: testLabel,
        },
      ],
    },
    subject: createReference(patient),
    basedOn: [createReference(serviceRequest)],
    effectiveDateTime: formValues.dateReceived?.toISOString(),
    hasMember: childObservations.map(createReference),
  });
};

const processGroupItem = async (
  medplum: MedplumClient,
  patient: Patient,
  serviceRequest: ServiceRequest,
  responseItem: QuestionnaireResponseItem,
  questionnaireDefinition: QuestionnaireResponseItem,
  dateReceived: Date | undefined,
) => {
  const resultItem = findNestedItemBySuffix(responseItem.item, "-result");
  const bacteriaAmountItem = findNestedItemBySuffix(responseItem.item, "-bacteria-amount");
  const additionalInfoItem = findNestedItemBySuffix(responseItem.item, "-additional-info");
  const interpretationItem = findNestedItemBySuffix(responseItem.item, "-interpretation");
  const referenceItem = findNestedItemBySuffix(responseItem.item, "-reference");

  const resultDefinition = findNestedItemBySuffix(questionnaireDefinition.item, "-result");
  const unit = resultDefinition
    ? (getExtensionValue(resultDefinition, Extensions.QuestionnaireFieldRightSection) as string)
    : undefined;

  const interpretationCoding = interpretationItem?.answer?.[0]?.valueCoding;
  const interpretation: CodeableConcept | undefined = interpretationCoding
    ? {
        coding: [interpretationCoding],
        text: interpretationCoding.display,
      }
    : undefined;

  // Check if the result is a coded value (valueCoding) or a numeric/string value
  const resultCoding = resultItem?.answer?.[0]?.valueCoding;
  const resultValue = extractAnswerValue(resultItem);
  const additionalInfo = additionalInfoItem?.answer?.[0]?.valueString?.trim();
  const bacteriaAmountDisplay = bacteriaAmountItem?.answer?.[0]?.valueCoding?.display;
  const resultDisplay = resultCoding
    ? formatCodedResultWithAdditionalInfo(resultCoding, additionalInfo)
    : undefined;

  const analyteName = questionnaireDefinition.text || "";
  const analyteDisplay = bacteriaAmountDisplay
    ? `${analyteName} (${bacteriaAmountDisplay})`
    : analyteName;

  const resultCodeableConcept: CodeableConcept | undefined = resultCoding
    ? {
        coding: [{ ...resultCoding, display: resultDisplay }],
        text: resultDisplay,
      }
    : undefined;

  return createManualObservation(
    medplum,
    patient,
    serviceRequest,
    analyteDisplay,
    resultValue ?? resultDisplay, // Use display as fallback for coded values
    dateReceived,
    unit,
    interpretation,
    referenceItem?.answer?.[0]?.valueString,
    {
      system: QuiltedHealthCodeSystems.ManualLabResults,
      code: questionnaireDefinition.linkId,
      display: analyteDisplay,
    },
    resultCodeableConcept,
  );
};

const createManualObservation = async (
  medplum: MedplumClient,
  patient: Patient,
  serviceRequest: ServiceRequest,
  codeText: string,
  value: string | number | undefined,
  dateReceived: Date | undefined,
  unit?: string,
  interpretation?: CodeableConcept,
  referenceRange?: string,
  coding?: Coding,
  valueCodeableConcept?: CodeableConcept,
) => {
  if (value === undefined || value === null || value === "") return undefined;

  return medplum.createResource<Observation>({
    resourceType: "Observation",
    status: "final",
    code: coding
      ? {
          coding: [coding],
          text: codeText,
        }
      : { text: codeText },
    subject: createReference(patient),
    basedOn: [createReference(serviceRequest)],
    effectiveDateTime: dateReceived?.toISOString(),
    valueCodeableConcept,
    valueQuantity: unit && !valueCodeableConcept ? { value: Number(value), unit } : undefined,
    valueString: !unit && !valueCodeableConcept ? String(value) : undefined,
    interpretation: interpretation ? [interpretation] : undefined,
    referenceRange: referenceRange ? [{ text: referenceRange }] : undefined,
    category: [
      {
        coding: [
          {
            system: Systems.HL7ObservationCategory,
            code: ObservationCategory.Laboratory,
            display: "Laboratory",
          },
        ],
      },
    ],
  });
};
